/**
 * deployment-header.js
 */

import React from 'react';
import { Tooltip, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { parseISO } from 'date-fns';
import { Types } from 'utils/constants';
import { UserContext } from 'contexts/user/user-context';
import useRecordStore from 'contexts/record-store';
import RouteLink from 'routing/route-link';
import Icon from 'components/icon';
import { TRACK_TYPE } from 'components/tracking/tracking-common';
import { ViewingMapContext } from 'components/tracking/map/viewing-map-context';
import Row from '../row/row';
import RowLocationDate from '../row/row-location-date';



const useStyles = makeStyles(theme => ({
  root: {
    height: 40,
    transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    backgroundColor: theme.palette.comp.recordRow.deployment.background,
    '&:hover': {
      backgroundColor: theme.palette.comp.recordRow.headerHover(theme.palette.comp.recordRow.deployment.background)
    }
  },
  detailContainer: {
    overflow: 'hidden',
  },
  mapIcon: {
    marginLeft: theme.spacing(1),
    color: theme.palette.secondary.colors[5]
  }
}));


const DeploymentHeader = React.memo(function (props) {
  const {
    id,
    basePath,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const { setMapParams, setIsDialogOpen } = React.useContext(ViewingMapContext);
  const recordStore = useRecordStore();
  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, id).ornull;

  if (!deploymentRecord) return null;

  const deployment = deploymentRecord.get();
  const date = parseISO(deployment.date);
  const trackingRecord = deployment.patrol?.patrolTypes?.[0]?.tracking;
  const hasTrack = user.role.isSupervisor && trackingRecord?.hasTrack;

  const routeLink = <RouteLink
    basePath={basePath}
    pathSectionName='deployment'
    pathItemName='view'
    pathParams={{ deploymentId: id }}
    className='absolute-fill record-link'
  />;


  return (
    <>
      <Row
        data-testid={id}
        data-testtype='header-deployment'
        recordType={Types.Record.DEPLOYMENT}
        isHeader
        className={`header ${classes.root}`}
        routeLink={routeLink}
        icon={<Icon type='deployment' />}
        textVariant='body2'
        title='Deployment'
        detail={<div className={`flex-hc ${classes.detailContainer}`}>
          <RowLocationDate
            location={deployment.location.locationName || deployment.location.address1 || 'no address'}
            date={date}
          />

          {/* supervisor tracking map icon */}
          {hasTrack &&
          <Tooltip arrow placement='top' title='View Tracking Map'>
            <IconButton 
              size='small' 
              className={classes.mapIcon} 
              onClick={async () => {
                setMapParams(TRACK_TYPE.DEPLOYMENT, deployment.id, trackingRecord.id);
                setIsDialogOpen(true);
              }}
            >
              <Icon type='map' />
            </IconButton>
          </Tooltip>}
        </div>}
        optional={<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {user.role.isHandler ? deployment.dog.name : ''}
          {user.role.isSupervisor ? deployment.handler.name : ''}
        </div>}
      />
    </>
  )
}, (prev, next) => {
  return (
    prev.id === next.id
  );
});

export default DeploymentHeader;